import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink } from '@apollo/client'

import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { sha256 } from 'crypto-hash'

import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import React, { useEffect, useState } from 'react'
import { useAuthContext } from './auth'
import { getCache } from './cache'
import { config } from './config'
import { logger } from './logger'

export const AuthorizedApolloProvider: React.FC = ({ children }) => {
    const { auth, user } = useAuthContext()

    const [client, setClient] = useState<ApolloClient<any> | null>(null)

    const handleLogout = async () => {
        await auth.signOut()
        window.location.href = '/signin'
    }

    const logoutLink = onError(({ response }) => {
        const unauthenticated = response?.errors?.some(
            ({ extensions }) => extensions.response?.status === 401
        )
        if (unauthenticated) handleLogout()
    })

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
            graphQLErrors.forEach(({ message, locations, path }) => {
                logger.error(`[GraphQL error]: Message: ${message}, Path: ${path}`)
            })
        if (networkError) logger.error(`[Network error]: ${networkError}`)
    })

    const authLink = setContext(async (_, { headers = {} }) => {
        const idToken = await auth
            .currentSession()
            .then((session) => session.getIdToken()?.getJwtToken())

        if (idToken) {
            headers.Authorization = `Bearer ${idToken}`
        }

        return {
            headers,
        }
    })

    const httpLink = createHttpLink({
        uri: config.graphqlEndpoint,
        credentials: 'same-origin',
    })

    const persistedQueriesLink = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true,
    })

    useEffect(() => {
        getCache(user).then((cache) => {
            const _client = new ApolloClient({
                link: ApolloLink.from([
                    authLink,
                    logoutLink,
                    errorLink,
                    persistedQueriesLink,
                    httpLink,
                ]),
                cache,
                connectToDevTools: true,
                defaultOptions: {
                    mutate: {
                        errorPolicy: 'none',
                    },
                    query: {
                        fetchPolicy: 'cache-first',
                        errorPolicy: 'all',
                    },
                    watchQuery: {
                        errorPolicy: 'all',
                    },
                },
            })
            setClient(_client)
        })
    }, [user])

    if (!client) {
        return null
    }

    return <ApolloProvider client={client}>{children}</ApolloProvider>
}
